import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';

const AdvertisersCarousel = ({ advertisers }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const itemsPerView = 4;
  const totalSlides = advertisers ? Math.ceil(advertisers.length / itemsPerView) : 0;

  useEffect(() => {
    if (!advertisers || advertisers.length === 0) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % totalSlides);
    }, 5000);
    return () => clearInterval(interval);
  }, [totalSlides, advertisers]);

  if (!advertisers || advertisers.length === 0) return null;

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % totalSlides);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + totalSlides) % totalSlides);
  };

  const visibleAdvertisers = advertisers.slice(
    currentIndex * itemsPerView,
    (currentIndex + 1) * itemsPerView
  );

  return (
    <section className="py-20 bg-[var(--ink)]">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="font-display text-5xl md:text-6xl font-medium tracking-tight text-white mb-4">
            Our <span className="text-[var(--madder)]">Advertisers</span>
          </h2>
          <p className="text-gray-400 text-lg">
            Supporting local businesses and community partners
          </p>
        </div>

        <div className="relative">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {visibleAdvertisers.map((advertiser) => (
              <div
                key={advertiser.id}
                className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 group cursor-pointer transform hover:scale-105"
                onClick={() => window.open(advertiser.link, '_blank')}
              >
                <div className="relative h-40 bg-gradient-to-br from-slate-100 to-slate-200 overflow-hidden">
                  <img
                    src={advertiser.logo}
                    alt={advertiser.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    onError={(e) => { e.target.src = '/api/files/paisley-org/wp-content/uploads/2025/09/Paisley-skyline-from-abbey-770x513-1.jpg'; }}
                  />
                  <div className="absolute inset-0 bg-[var(--madder)]/0 group-hover:bg-[var(--madder)]/10 transition-colors duration-300"></div>
                </div>
                <div className="p-4 bg-white">
                  <p className="text-sm font-semibold text-slate-900 text-center truncate">{advertiser.name}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Navigation Buttons */}
          <div className="flex justify-center gap-4 mt-8">
            <Button
              variant="outline"
              size="icon"
              onClick={prevSlide}
              className="bg-white/10 border-white/20 text-white hover:bg-white/20 backdrop-blur-sm"
            >
              <ChevronLeft size={20} />
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={nextSlide}
              className="bg-white/10 border-white/20 text-white hover:bg-white/20 backdrop-blur-sm"
            >
              <ChevronRight size={20} />
            </Button>
          </div>

          {/* Dots Indicator */}
          <div className="flex justify-center gap-2 mt-6">
            {Array.from({ length: totalSlides }).map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`h-2 rounded-full transition-all duration-300 ${
                  index === currentIndex
                    ? 'w-8 bg-cyan-400'
                    : 'w-2 bg-white/30 hover:bg-white/50'
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default AdvertisersCarousel;
